import { DataLoader } from 'src/app/services/data-loader/data-loader';
import { DataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { standardRun } from 'src/app/services/data-loader/utils/standard-run';
import { QueryCacheService } from 'src/app/services/query-cache.service';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import { generateTableFullPath } from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { BiVizDashboardMasterPlayerKpisDailySqlMap } from '../sql-maps/biviz-dashboard-master-users-kpis-daily-sql-map';

export interface UsersDaysMonthlyD30DataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    createdInstanceId: string;
    gcpProjectId: GcpProjectId | (() => GcpProjectId);
    queryCacheService: QueryCacheService;
}

export class UsersDaysMonthlyD30DataLoaderFactory extends Factory<
    DataLoader,
    UsersDaysMonthlyD30DataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string = 'UsersDaysMonthlyD30DataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(options: UsersDaysMonthlyD30DataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getRetentionByFirstOpenDateDataLoaderFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext,
            cancellationToken
        ) {
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap =
                    BiVizDashboardMasterPlayerKpisDailySqlMap.sqlMap;

                // filter from context COMPANY_REVENUE_TYPE and REVENUE_PAYER
                const datasourceColumns = queryContext.datasourceColumns;
                // const datasourceColumns = queryContext.datasourceColumns.filter(
                //     (dsCol) =>
                //         dsCol.id !== 'REVENUE_TYPE'
                // );

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const {
                    splitSelect,
                    splitGroupBy,
                    where,
                    // is_in_context_app_version,
                    // unnestDatasourceColumn,
                    // join,
                } =
                    BiVizDashboardMasterPlayerKpisDailySqlMap.compileSQLStatements(
                        dsColumns.present,
                        queryContext.settings,
                        { gcpProjectId: resolvedGcpProjectId },
                        {
                            where: [
                                `date <= date_dim.days_ago_30`,
                                `date >= date_dim.months_ago_15`,
                                // 'day_no BETWEEN 1 AND 30',
                                // 'validation_flag IS NULL',
                            ],
                        }
                    );

                const tableFullPath = generateTableFullPath(
                    resolvedGcpProjectId,
                    datasourceSqlMap.dataset,
                    datasourceSqlMap.tableId
                );
                // const lastDateQuery = `(SELECT MAX(date) AS last_date FROM \`${tableFullPath}\`)`;
                // const lastDateQueryResponse = await DataLoader.runQuery(
                //     lastDateQuery,
                //     options.funcService
                // );
                // const lastDate = (lastDateQueryResponse.result[0] as any)
                //     .last_date.value;

                const query = `
-- DataLoaderId: ${this.instanceId}

WITH date_dim AS (
    SELECT *
      , DATE_SUB(current_d, INTERVAL 30 DAY) AS days_ago_30
      , DATE_SUB(current_m, INTERVAL 15 MONTH) AS months_ago_15
    FROM 
    (
      SELECT MAX(date) AS current_d 
        , DATE_TRUNC(MAX(date), MONTH) AS current_m
      FROM \`${tableFullPath}\`
    )
  )
  
  SELECT
  ${splitGroupBy}date
    , SUM(weight * avg_lifetime_d30) AS avg_user_lifetime_d30_monthly
  FROM
  (
    SELECT *
      , initial_cohort_d30 / SUM(initial_cohort_d30) OVER (PARTITION BY ${splitGroupBy}date) AS weight
    FROM
    (
      SELECT ${splitSelect}DATE_TRUNC(date, MONTH) AS date
        , daily_cohort_kpis.avg_lifetime_d30
        , daily_cohort_kpis.initial_cohort_d30
      FROM \`${tableFullPath}\`,
        date_dim
      ${where}
    )
  )
  GROUP BY ${splitGroupBy}date
  ORDER BY ${splitGroupBy}date
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
